import { Injectable } from '@angular/core';
import { LoggingService } from '@logging/logging.service';
import { BehaviorSubject, Observable, combineLatest, map, startWith, switchMap, of } from 'rxjs';

export interface MobileOverrideProvider {
  id: string;
  isActive$: Observable<boolean>;
  onAction: () => void;
  icon?: string;
  label?: string;
  priority?: number; // Higher priority takes precedence
}

@Injectable({
  providedIn: 'root'
})
export class MobileOverrideService {
  private _providers$ = new BehaviorSubject<MobileOverrideProvider[]>([]);
  private _activeOverride$ = new BehaviorSubject<MobileOverrideProvider | null>(null);

  readonly providers$ = this._providers$.asObservable();
  readonly activeOverride$ = this._activeOverride$.asObservable();

  constructor(protected logger: LoggingService) {
    this.providers$
      .pipe(
        switchMap((providers) => {
          this.logger.trace(
            'MobileOverrideService: Processing providers:',
            providers.map((p) => p.id)
          );
          if (providers.length === 0) {
            this.logger.trace('MobileOverrideService: No providers, returning null');
            return of(null);
          }

          const activeStates$ = providers.map((provider) =>
            provider.isActive$.pipe(
              startWith(false),
              map((isActive) => {
                this.logger.trace(`MobileOverrideService: Provider ${provider.id} isActive:`, isActive);
                return { provider, isActive };
              })
            )
          );

          return combineLatest(activeStates$).pipe(
            map((states) => {
              this.logger.trace(
                'MobileOverrideService: Combined states:',
                states.map((s) => `${s.provider.id}: ${s.isActive}`)
              );

              const activeProviders = states.filter((state) => state.isActive).map((state) => state.provider);

              if (activeProviders.length === 0) {
                this.logger.trace('MobileOverrideService: No active providers');
                return null;
              }

              // Return the provider with the highest priority
              const result = activeProviders.reduce((highest, current) =>
                (current.priority || 0) > (highest.priority || 0) ? current : highest
              );

              this.logger.trace('MobileOverrideService: Active override:', result.id);
              return result;
            })
          );
        }),
        startWith(null)
      )
      .subscribe((activeOverride) => {
        this._activeOverride$.next(activeOverride);
      });
  }

  registerProvider(provider: MobileOverrideProvider): void {
    this.logger.trace('MobileOverrideService: Registering provider:', provider.id);
    const currentProviders = this._providers$.value;
    const existingIndex = currentProviders.findIndex((p) => p.id === provider.id);

    if (existingIndex >= 0) {
      currentProviders[existingIndex] = provider;
      this.logger.trace('MobileOverrideService: Updated existing provider:', provider.id);
    } else {
      currentProviders.push(provider);
      this.logger.trace('MobileOverrideService: Added new provider:', provider.id);
    }

    this._providers$.next([...currentProviders]);
    this.logger.trace('MobileOverrideService: Total providers:', currentProviders.length);
  }

  unregisterProvider(id: string): void {
    const currentProviders = this._providers$.value;
    const filteredProviders = currentProviders.filter((p) => p.id !== id);
    this._providers$.next(filteredProviders);
  }

  createMobileMenuOverride() {
    const self = this; // Capture 'this' context
    return {
      isOverrideActive$: this.activeOverride$.pipe(map((override) => override !== null)),
      onOverrideAction: () => {
        // Get the current active override synchronously
        const currentProviders = self._providers$.value;
        let activeProvider: MobileOverrideProvider | null = null;

        // Check which provider is currently active
        for (const provider of currentProviders) {
          let isActive = false;
          // Get current value synchronously
          provider.isActive$
            .pipe(map((active) => (isActive = active)))
            .subscribe()
            .unsubscribe();

          if (isActive) {
            if (!activeProvider || (provider.priority || 0) > (activeProvider.priority || 0)) {
              activeProvider = provider;
            }
          }
        }

        if (activeProvider) {
          self.logger.trace('MobileOverrideService: Calling onAction for provider:', activeProvider.id);
          activeProvider.onAction();
        } else {
          self.logger.trace('MobileOverrideService: No active provider found');
        }
      },
      get overrideIcon() {
        const activeOverride = self._activeOverride$.getValue();
        const icon = activeOverride?.icon || 'x';
        console.log('[MobileOverrideService] Getting override icon:', icon, 'from provider:', activeOverride?.id);
        return icon;
      },
      get overrideLabel() {
        const activeOverride = self._activeOverride$.getValue();
        const label = activeOverride?.label || 'Close';
        console.log('[MobileOverrideService] Getting override label:', label, 'from provider:', activeOverride?.id);
        return label;
      }
    };
  }
}
